import { useMutation } from '@tanstack/react-query';
import * as z from 'zod';

import axios from 'src/lib/axios';
import { MutationConfig } from 'src/lib/react-query';
import { GeneralSmartParkError } from 'src/registry/types/axios';
import { validationMessagesMap } from 'src/utils/messages';
import { formatResourcePath } from 'src/utils/regex';
import { DISPLAY_ACTION, DISPLAY_IDLE_ACTION } from 'src/features/actions/utils/types';

type SimulateDisplayRequestFormSchemaValues = z.infer<typeof simulateDisplayRequestFormSchema>;
export type SimulateDisplayRequestFnType = typeof simulateDisplayRequest;

const simulateDisplayRequestFormSchema = z
    .object({
        componentId: z.string().nonempty(),
        action: z.enum(DISPLAY_ACTION).optional(),
        idle_action: z.enum(DISPLAY_IDLE_ACTION).optional(),
        license_plate: z.string().optional(),
        title: z.string().optional(),
        subtitle: z.string().optional(),
        setting_json_placeholders: z
            .object({
                free_spaces: z.number().int().optional(),
                reason: z.string().optional(),
                total_remaining_amount: z.string().optional(),
                amount: z.number().optional(),
                amount_paid: z.number().optional(),
                currency: z.string().optional(),
            })
            .optional(),
    })
    .superRefine((data, ctx) => {
        if (!data.action && !data.idle_action) {
            ctx.addIssue({
                path: ['action'],
                message: validationMessagesMap.validationErrorActionIdleActionDisplay,
                code: 'custom',
            });
            ctx.addIssue({
                path: ['idle_action'],
                message: validationMessagesMap.validationErrorActionIdleActionDisplay,
                code: 'custom',
            });
        }
    });

const RESOURCE = `sp-core/:parkingId/system/component/:componentId/simulate-display-request/`;

interface DTO extends SimulateDisplayRequestFormSchemaValues {
    componentId: string;
    parkingId: string;
}

type UseSimulateDisplayRequest = MutationConfig<SimulateDisplayRequestFnType, GeneralSmartParkError>;

const simulateDisplayRequest = ({ componentId, parkingId, ...data }: DTO): Promise<void> => {
    return axios.post(formatResourcePath(RESOURCE, { componentId, parkingId }), data);
};

export const useSimulateDisplayRequest = (options: UseSimulateDisplayRequest = {}) =>
    useMutation({
        mutationFn: simulateDisplayRequest,
        ...options,
    });
export { simulateDisplayRequestFormSchema, RESOURCE as SIMULATE_DISPLAY_REQUEST_RESOURCE };
export type { SimulateDisplayRequestFormSchemaValues };
